// TypeScript React
import { useCallback, useEffect, useState } from 'react';
import {
    fetchNewEdisonThreadId,
    getAIResearchMessages,
    postAIResearchMessage,
    postEdisonMessage,
    postEdisonFeedback,
} from 'services/reporting';
import { AiAgentTypes } from '../types/ai.types';

type Role = 'user' | 'assistant' | 'system';

type RowTypeField = { name: string };
type ResultSet = {
    resultSetMetaData: { rowType: RowTypeField[] };
    data: string[][];
};

function buildMarkdownTable(title: string, resultSet: ResultSet): string {
    const headers = resultSet.resultSetMetaData.rowType.map((rt) => rt.name).join(' | ');
    const separator = resultSet.resultSetMetaData.rowType.map(() => '---').join(' | ');
    const rows = resultSet.data.map((row) => `| ${row.join(' | ')} |`).join('\n');
    return `**Table:${title}**\n| ${headers} |\n| ${separator} |\n${rows}\n`;
}

export interface EdisonChatMessage {
    role: Role;
    deltaContent: string;
    content: string[];
    details?: AgentDetails; // optional metadata to show in a collapsible panel
    hasDetails: boolean;
    messageId?: number;
    requestId?: string;
    feedback?: { isPositive: boolean; submitted: boolean };
}

export interface AgentDetails {
    sql?: string;
    citations?: Array<{ title?: string; url?: string; snippet?: string }>;
    toolRaw?: unknown; // full tool result if you want to show raw JSON
    charts?: AgentChart[]; // optional charts emitted by tools
    thinking?: string;
    toolResult?: Array<{ name: string; sql: string }>;
}

export interface AgentChart {
    type: 'vega_lite_chart';
    title?: string;
    spec: any; // VisualizationSpec
    data?: any[];
}

export function useEdisonAgent(
    agentType: AiAgentTypes = AiAgentTypes.EDISON,
    contextType?: string,
    contextId?: number
) {
    const [messages, setMessages] = useState<EdisonChatMessage[]>([]);
    const [isStreaming, setIsStreaming] = useState(false);
    const [messagesThreadId, setMessagesThreadId] = useState<string>();

    const submitFeedback = useCallback(
        async (isPositive: boolean, requestId: string, feedbackText?: string) => {
            if (!requestId) {
                return false;
            }
            try {
                await postEdisonFeedback(requestId, isPositive, feedbackText);
                console.log('Submitting Feedback:', { requestId: requestId, feedbackText, isPositive });
                const success = true;
                if (success) {
                    setMessages(
                        (prev) =>
                            prev.map((msg) =>
                                msg.requestId === requestId
                                    ? { ...msg, feedback: { isPositive, submitted: true } }
                                    : msg
                            ) //keeps each response in correct feedback state
                    );
                }
                return true;
            } catch (error) {
                console.error('Failed to submit feedback', error);
                return false;
            }
        },
        [postEdisonFeedback, setMessages]
    );

    const initializeThreadId = useCallback(
        async (startNewConversation?: boolean) => {
            let threadId;
            switch (agentType) {
                case AiAgentTypes.RESEARCH:
                    if (!contextType || !contextId) {
                        console.error('No context for AI Research agent');
                        return;
                    }
                    setIsStreaming(true);
                    await getAIResearchMessages(contextType, contextId, startNewConversation).then((result) => {
                        const loadedMessages: EdisonChatMessage[] = result.map((msg: any) => ({
                            role: msg.role,
                            content: [msg.content],
                            hasDetails: false,
                            deltaContent: '',
                        }));
                        setMessages(loadedMessages);
                        setIsStreaming(false);
                    });
                    break;
                case AiAgentTypes.EDISON:
                    await fetchNewEdisonThreadId().then((result) => (threadId = result.threadId));
                    break;
            }
            setMessagesThreadId(threadId);
        },
        [agentType, contextType, contextId]
    );

    const createNewThread = useCallback(async () => {
        if (messagesThreadId) return;
        await initializeThreadId();
    }, [messagesThreadId, initializeThreadId]);

    useEffect(() => {
        setMessages([]);
        setMessagesThreadId(undefined);
        if (agentType === AiAgentTypes.RESEARCH && contextType && contextId) {
            initializeThreadId();
        }
    }, [contextType, contextId]);

    const send = useCallback(
        async (userText: string) => {
            const lastMsg = messages[messages.length - 1];
            setMessages((prev) => {
                return [...prev, { role: 'user', content: [userText], hasDetails: false, deltaContent: '' }];
            });

            let resp;
            switch (agentType.toLowerCase()) {
                case AiAgentTypes.RESEARCH.valueOf():
                    if (!contextType || !contextId) {
                        console.error('No context for research agent');
                        return;
                    }
                    // Initial implementation of Perplexity integration doesn't support streaming, instead doing a full call.
                    // So go ahead and set isStreaming to true to show loading state before kicking off the call.
                    setIsStreaming(true);
                    resp = await postAIResearchMessage(contextType, contextId, userText, agentType);
                    break;
                default:
                    // For Edison agent, we will handle streaming below
                    resp = await postEdisonMessage(userText, messagesThreadId, lastMsg?.messageId || 0);
                    break;
            }

            if (!resp.ok || !resp.body) {
                throw new Error('Failed to start stream');
            }

            const curMsg: EdisonChatMessage = {
                role: 'assistant',
                content: [],
                hasDetails: false,
                details: { charts: [] },
                deltaContent: '',
                requestId: resp.headers.get('X-Snowflake-Request-Id') || undefined,
            };
            setMessages((prev) => [...prev, curMsg]);
            setIsStreaming(true);

            const reader = resp.body.getReader();
            const decoder = new TextDecoder();
            let buf = '';
            const loopCond = true;

            try {
                while (loopCond) {
                    const { value, done } = await reader.read();
                    if (done) break;
                    buf += decoder.decode(value, { stream: true });

                    const frames = buf.split('\n\n');
                    buf = frames.pop() ?? '';

                    for (const frame of frames) {
                        const lines = frame.split('\n');
                        const dataLine = lines.find((l) => l.startsWith('data:'));
                        const eventLine = lines.find((l) => l.startsWith('event:'));

                        if (!dataLine) continue;

                        const data = dataLine.replace(/^data:\s?/, '');

                        if (data === '[DONE]') continue;

                        if (!eventLine) continue;

                        const event = eventLine.replace(/^event:\s?/, '');

                        try {
                            const evt = JSON.parse(data);
                            console.log('agent reponse: ', evt);

                            const last = curMsg;
                            if (last?.role === 'assistant') {
                                switch (event) {
                                    case 'response.text.delta':
                                        last.deltaContent = last.deltaContent + evt.text;
                                        break;
                                    case 'response.text':
                                        last.content[evt.content_index] = evt.text;
                                        break;
                                    case 'response.thinking':
                                        if (!last.details) last.details = {};
                                        last.details.thinking = evt.text;
                                        last.hasDetails = true;
                                        break;
                                    case 'response.chart':
                                        if (!last.details) last.details = {};
                                        if (!last.details.charts) last.details.charts = [];
                                        last.details.charts.push({
                                            type: 'vega_lite_chart',
                                            spec: evt.chart_spec,
                                        });
                                        last.hasDetails = true;
                                        break;
                                    case 'response.search_results':
                                        if (!last.details) last.details = {};
                                        last.details.citations = evt.searchResults.map((res: any) => ({
                                            title: res.title,
                                            url: res.url,
                                            snippet: res.snippet,
                                        }));
                                        last.hasDetails = true;
                                        break;
                                    case 'response.sql':
                                        if (!last.details) last.details = {};
                                        last.details.sql = evt.sql;
                                        last.hasDetails = true;
                                        break;
                                    case 'response.table':
                                        last.content[evt.content_index] = buildMarkdownTable(evt.title, evt.result_set);
                                        break;
                                    case 'response.tool_result':
                                        if (!last.details) last.details = {};
                                        if (!last.details.toolResult) last.details.toolResult = [];
                                        last.details.toolResult.push({
                                            name: evt.name,
                                            sql: evt.content?.[0]?.json?.sql,
                                        });
                                        last.hasDetails = true;
                                        break;
                                    case 'metadata':
                                        last.messageId = evt.metadata.message_id;
                                        break;
                                }

                                setMessages((prev) => {
                                    const next = [...prev];
                                    next[next.length - 1] = {
                                        ...curMsg,
                                        details: { ...(curMsg.details || { charts: [] }) },
                                    };
                                    return next;
                                });
                            }
                        } catch {
                            // ignore non-JSON data frames
                        }
                    }
                }
            } finally {
                setIsStreaming(false);
            }
        },
        [agentType, messages, messagesThreadId]
    );

    const reset = useCallback(() => {
        setMessages([]);
        initializeThreadId(true);
    }, [initializeThreadId]);

    return {
        messages,
        isStreaming,
        send,
        reset,
        createNewThread,
        submitFeedback,
    };
}
